"use client";

import React from "react";
import Image from "next/image";
import Breadcrumb from "@/components/common/Breadcrumb";
import citiesData from "@/data/cities.json";

interface CityPageProps {
  slug: string;
}

export default function CityPage({ slug }: CityPageProps) {
  const city = citiesData.find((c) => c.slug === slug);

  if (!city) {
    return <div className="text-center py-20 text-black">Service Area not found</div>;
  }

  return (
    <div className="w-full bg-[#f9f9f9] text-[#1e1e1e] font-sans pb-[100px] relative">
      <Breadcrumb
        title={`${city.city} Limo Service`}
        paths={[
          { label: "HOME", href: "/" },
          { label: "CITIES", href: "#" },
          { label: city.city },
        ]}
      />

      {/* Decorative center lines in the background */}
      <div className="absolute top-[500px] bottom-0 left-1/2 w-[1px] bg-[#eee] z-0 -translate-x-1/2 hidden lg:block" />
      <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-[#eee] z-0 hidden lg:block" />

      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-[1700px] mx-auto px-3 sm:px-6 lg:px-[50px] mt-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          
          {/* Left Column (Main Content) */}
          <div className="w-full lg:w-1/2 xl:w-[60%]">
            <div className="w-full bg-white p-6 sm:p-10 border border-[#eee] shadow-[0_0_20px_rgba(0,0,0,0.02)] h-full">
              
              <div className="mb-8">
                {/* Title with grey border and yellow dash on right */}
                <div className="relative pb-4 mb-4 border-b border-[#eee]">
                  <h1 className="text-[20px] sm:text-[24px] lg:text-[28px] font-bold text-[#191919] leading-tight pr-10">
                    Limo Service {city.city} – Professional and Punctual Transportation
                  </h1>
                  <div className="absolute bottom-[-1px] right-0 w-8 h-[2px] bg-[#FFCD00]" />
                </div>
                
                <div className="space-y-4 text-[#000] text-[16px] sm:text-[17px] leading-[1.8] font-medium mt-6">
                  {city.content.map((paragraph, idx) => {
                    // Extracting "Why Choose Heartland Limo in [City]:" as a subtitle if it exists
                    if (paragraph.startsWith("Why Choose Heartland Limo")) {
                      return (
                        <div key={idx} className="mt-8 mb-4">
                           <h2 className="text-[18px] sm:text-[20px] font-bold text-[#191919]">
                             {paragraph}
                           </h2>
                        </div>
                      )
                    }
                    // Handle bullet points inside the content array
                    if (paragraph.includes("Local expertise:") || paragraph.includes("Privacy and discretion:") || paragraph.includes("Extended service:")) {
                      const [boldPart, restPart] = paragraph.split(":");
                      if (restPart) {
                        return (
                          <div key={idx} className="flex items-start mt-2">
                             <span className="w-1.5 h-1.5 bg-[#FFCD00] mt-2 mr-3 shrink-0 rounded-full" />
                             <p><strong>{boldPart}:</strong>{restPart}</p>
                          </div>
                        )
                      }
                    }
                    
                    return <p key={idx}>{paragraph}</p>;
                  })}
                </div>
              </div>

            </div>
          </div>

          {/* Right Column (Image) */}
          <div className="w-full lg:w-1/2 xl:w-[40%] flex items-start">
            <div className="w-full bg-white p-4 border border-[#eee] shadow-[0_0_20px_rgba(0,0,0,0.02)] sticky top-24">
               <div className="relative w-full aspect-[4/3] rounded-[4px] overflow-hidden group">
                  <Image
                    src={city.imageUrl}
                    alt={`${city.city} Limo Service`}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                  <div className="absolute inset-0 border-[4px] border-[#FFCD00] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
               </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
